import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../../chat/api";

/**
 * Click-and-drag-to-create-a-new-field gesture (optimistic).
 *
 * Flow:
 *   1. User picks the Text tool → drawMode flips on
 *   2. mousedown on a page wrapper → setDrawing({...}); mousemove → live preview
 *   3. mouseup → `onOptimisticAdd(tempField)` renders the box instantly, then
 *      POSTs `/jobs/<jobId>/fields/` in the background → `onCreated(real, tempId)`
 *      swaps in the saved record (or `onFailed(tempId)` rolls it back).
 */
let _drawSeq = 0;
const tempId = () => `temp_${Date.now()}_${_drawSeq++}`;

export default function useDrawGesture({
  jobId,
  isFinalizedView,
  onOptimisticAdd,
  onCreated,
  onFailed,
}) {
  const [drawMode, setDrawMode] = useState(false);
  const [drawing, setDrawing] = useState(null);
  // Creation is optimistic (instant), so there's no blocking "creating" state.
  const creatingField = false;

  const handlePageMouseDown = useCallback(
    (e, pageIndex, pageEl, scale) => {
      if (!drawMode || creatingField || isFinalizedView || !pageEl) return;
      if (e.button !== 0) return;
      const rect = pageEl.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      setDrawing({
        pageIndex,
        startX: x,
        startY: y,
        currentX: x,
        currentY: y,
        pageRect: {
          left: rect.left,
          top: rect.top,
          width: rect.width,
          height: rect.height,
        },
        scale,
      });
      e.preventDefault();
    },
    [drawMode, creatingField, isFinalizedView]
  );

  const finalizeDrawing = useCallback(
    async (d) => {
      const x0 = Math.min(d.startX, d.currentX) / d.scale;
      const y0 = Math.min(d.startY, d.currentY) / d.scale;
      const x1 = Math.max(d.startX, d.currentX) / d.scale;
      const y1 = Math.max(d.startY, d.currentY) / d.scale;
      // Reject tiny rectangles — likely an accidental click instead of a drag.
      if (x1 - x0 < 4 || y1 - y0 < 4) return;

      const bbox = [x0, y0, x1, y1];
      const id = tempId();
      // Show the box on the PDF instantly, then persist in the background.
      const optimistic = {
        id,
        field_id: id,
        field_type: "text",
        label: "",
        section: "",
        page: d.pageIndex,
        bbox,
        write_targets: [bbox],
        options: null,
        answer: null,
        confidence: null,
        sources: [],
        user_overridden: true,
        needs_review: false,
        _pending: true,
      };
      setDrawMode(false);
      onOptimisticAdd?.(optimistic);
      try {
        const res = await api.post(`/api/v1/rfp/autopdf/jobs/${jobId}/fields/`, {
          page: d.pageIndex,
          bbox,
          answer: null,
        });
        if (![200, 201].includes(res.status)) throw new Error("Create field failed");
        onCreated?.(res.data, id);
      } catch (err) {
        console.error("Field create failed:", err);
        onFailed?.(id);
        toast.error(
          err.response?.data?.error || err.message || "Failed to create field."
        );
      }
    },
    [jobId, onOptimisticAdd, onCreated, onFailed]
  );

  // Global listeners while a draw gesture is active.
  useEffect(() => {
    if (!drawing) return;
    const handleMove = (e) => {
      setDrawing((prev) => {
        if (!prev) return prev;
        const x = Math.max(
          0,
          Math.min(prev.pageRect.width, e.clientX - prev.pageRect.left)
        );
        const y = Math.max(
          0,
          Math.min(prev.pageRect.height, e.clientY - prev.pageRect.top)
        );
        return { ...prev, currentX: x, currentY: y };
      });
    };
    const handleUp = () => {
      setDrawing((prev) => {
        if (prev) finalizeDrawing(prev);
        return null;
      });
    };
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawing != null]);

  return {
    drawMode,
    setDrawMode,
    drawing,
    creatingField,
    handlePageMouseDown,
  };
}
